import {
  GAME_PICTURE_FIELD,
  gamePictureLogKind,
  type CatalogueChangeReason,
  type CatalogueWriteResult,
  type ErrorCode,
  type GameProviderDriver,
} from '@dayon/contracts';

/**
 * ADDING AND EDITING catalogue rows — the vocabulary and the sentences.
 *
 * Four routes landed together and they are the three controls this screen drew
 * hard-disabled for its whole life: Add game, Add provider, and Edit
 * configuration, which is TWO routes because the drawer's button edits a TITLE
 * and the Providers tab's two columns belong to a PROVIDER.
 *
 *   · POST  /api/games/providers            — a provider, born DISABLED
 *   · POST  /api/games                      — a title, born DISABLED
 *   · PATCH /api/games/:id/config           — a title's name, category, RTP
 *   · PATCH /api/games/providers/:id/config — certified_at, revenue_share_bp
 *
 * Kept out of the screen for the reason `provider-status.ts` is: the wording is
 * read and argued about more often than the markup, and it belongs somewhere it
 * can be read without it.
 */

/**
 * The five reasons a catalogue row is created or edited.
 *
 * FOUR ARE SHARED WITH THE STATUS LOGS, deliberately and with the same labels,
 * so `split_part(reason, ' — ', 1)` groups across all four logs on the
 * Configuration log tab and a quarterly report needs no translation table.
 *
 * `CATALOGUE_CORRECTION` is the one code no status log needs and this one
 * cannot do without: "the name was wrong", "the declared RTP was typed as 9600
 * and the certificate says 9650". A correction is not an incident and not a
 * commercial decision, and filing it as either makes both counts wrong.
 */
export const CATALOGUE_REASONS = [
  { code: 'CATALOGUE_CORRECTION', label: 'Correction to the record' },
  { code: 'CATALOGUE_LAUNCH', label: 'Catalogue launch or relaunch' },
  { code: 'COMMERCIAL_DECISION', label: 'Commercial decision' },
  { code: 'COMPLIANCE_HOLD', label: 'Compliance or certification hold' },
  { code: 'PROVIDER_INCIDENT', label: 'Provider incident or failed integration test' },
] as const satisfies readonly { code: CatalogueChangeReason; label: string }[];

/** A creation IS a launch; an edit is a correction until told otherwise. */
export const DEFAULT_CREATE_REASON: CatalogueChangeReason = 'CATALOGUE_LAUNCH';
export const DEFAULT_EDIT_REASON: CatalogueChangeReason = 'CATALOGUE_CORRECTION';

/**
 * The field names the routes speak, in words an operator reads.
 *
 * The log stores the CONTRACT's field name — `declaredRtpBp`, not
 * `declared_rtp_bp` — so this map is keyed on that, and the Configuration log
 * tab and every result sentence share it. An unknown key is RETURNED AS ITSELF
 * rather than swallowed: a field this map has not caught up with must still
 * appear as a word, exactly as `gameSubCategoryLabel` insists for a table type.
 */
export const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  category: 'Category',
  subCategory: 'Table type',
  declaredRtpBp: 'Declared RTP',
  driver: 'Driver',
  certifiedAt: 'Certified',
  revenueShareBp: 'Revenue share',
  /* A game's picture, from its own three writes (`game-picture.ts` in the API). */
  [GAME_PICTURE_FIELD]: 'Picture',
};

export const fieldLabel = (field: string): string => FIELD_LABELS[field] ?? field;

/**
 * A stored value as a screen renders it.
 *
 * Both config logs store TEXT on both ends whatever the column's type is — see
 * `690_catalogue_writes.sql` — so basis points arrive as the string '9650' and
 * must be rendered as the percentage a human typed. Two fields are basis points
 * and they mean different things (an RTP and a revenue share), so the FIELD
 * decides the formatting, never the shape of the value.
 *
 * NULL IS RENDERED AS A WORD, not as an empty cell: "not recorded" for a field
 * that was never set is a different fact from a zero, and the Providers tab has
 * said so in a notice since before either column existed.
 *
 * THE PICTURE IS READ BEFORE THE NULL BRANCH, because its null is not "not
 * recorded": it is a real state, no picture at all, and the tile draws the
 * game's initials. Its other ends are stored as `uploaded:<key>` or
 * `provider:<address>` — exact, so a log row's two ends always differ — and are
 * read here as the KIND of picture, never as a key or an address an operator
 * can do nothing with. A value this module did not write is shown as itself.
 */
export function formatFieldValue(field: string, value: string | null): string {
  if (field === GAME_PICTURE_FIELD) {
    const kind = gamePictureLogKind(value);
    if (kind === 'UPLOADED') return 'Uploaded picture';
    if (kind === 'PROVIDER') return "Provider's picture";
    if (kind === 'NONE') return 'Initials, no picture';
    return value ?? 'Initials, no picture';
  }
  if (value === null) return 'Not recorded';
  if (field === 'declaredRtpBp' || field === 'revenueShareBp') {
    const bp = Number(value);
    if (!Number.isFinite(bp)) return value;
    /* Basis points to a percentage: 9650 → 96.5%, 1200 → 12%. Trailing zeros
       dropped so a whole number does not read as a measurement it is not. */
    return `${String(Number((bp / 100).toFixed(2)))}%`;
  }
  return value;
}

/**
 * WHAT ACTUALLY MOVED, in one sentence.
 *
 * The route returns the fields that changed with both ends, so the console can
 * say which ones rather than "Saved" — and say nothing about a field it did not
 * touch. A toast reading "Saved" after an edit that silently dropped one of two
 * fields is how an operator comes to trust a screen that is wrong.
 */
export function movesSentence(result: CatalogueWriteResult): string {
  if (result.changed.length === 0) return `${result.name} was not changed.`;
  const parts = result.changed.map(
    (m) => `${fieldLabel(m.field)} ${formatFieldValue(m.field, m.from)} → ${formatFieldValue(m.field, m.to)}`,
  );
  return `${result.name}: ${parts.join('; ')}. The reason is in the Configuration log.`;
}

/**
 * THE DRIVER A FORM MAY CREATE, with the sentence that says why it is the only
 * one — as VISIBLE TEXT beside the control, never as a title attribute.
 *
 * A driver is CODE. MOCK needs none beyond what ships and opens nothing by
 * construction. SA needs credentials, an outbound host allowlist and a callback
 * IP allowlist that only a deployment supplies. LIVE names no integration at
 * all. The route refuses the other two by name; this is the form agreeing with
 * it in advance rather than letting an operator discover it after a click.
 */
export const CREATABLE_DRIVER: GameProviderDriver = 'MOCK';
export const DRIVER_LOCK_SENTENCE =
  'A provider created here runs on the mock driver, a stand-in with no real provider behind it: it will hold a shelf of titles and refuse ' +
  'every launch, which is what a catalogue fixture should do. A real driver is code — SA also ' +
  'needs deployment credentials and a callback allowlist — so an engineer switches it when the ' +
  'integration ships.';

/**
 * Basis points as an integer, from what an operator typed as a percentage.
 *
 * `''` is NOT ZERO and that is the whole reason this returns a three-way answer.
 * An empty box means "leave it alone" on an edit and "do not record one" on a
 * create; a typed 0 means "this costs nothing", which is a real commercial term.
 * Folding the two together would record a 0% revenue share for every provider
 * whose share nobody knows.
 */
export function percentToBasisPoints(
  raw: string,
): { kind: 'absent' } | { kind: 'value'; bp: number } | { kind: 'invalid'; why: string } {
  const text = raw.trim();
  if (text === '') return { kind: 'absent' };
  const pct = Number(text);
  if (!Number.isFinite(pct)) return { kind: 'invalid', why: 'Enter a percentage, such as 12.5.' };
  if (pct < 0) return { kind: 'invalid', why: 'A percentage cannot be negative.' };
  if (pct > 100) return { kind: 'invalid', why: 'A share above 100% is not a term — check the figure.' };
  const bp = Math.round(pct * 100);
  /* Rounded, and the rounding is REPORTED rather than silent: 12.345% cannot be
     stored in basis points, and an operator who typed it should be told what
     will be recorded instead of finding 12.35% later. */
  if (Math.abs(pct * 100 - bp) > 1e-9)
    return { kind: 'invalid', why: `Basis points hold two decimals — ${String(bp / 100)}% is the nearest.` };
  return { kind: 'value', bp };
}

export { NOTE_MAX, NOTE_MIN, noteError, noteIsValid } from './reason-note.ts';

/**
 * Why a refused catalogue write was refused, as a heading.
 *
 * The server's own sentence is always the message; this picks only the TITLE,
 * and it picks it from what this session KNOWS rather than by matching the
 * prose — `games.manage` and the two-factor rule both arrive as 403 with
 * different sentences, and matching on words breaks the first time one is
 * reworded.
 *
 * NO `TWO_FACTOR_REQUIRED` BRANCH, for the reason `refusalTitle` in
 * `provider-status.ts` has none: no back-office route can emit that code —
 * `requirePermission` re-throws every second-factor refusal as FORBIDDEN — so a
 * branch for it is a branch that cannot be reached.
 */
export function catalogueRefusalTitle(
  code: ErrorCode | undefined,
  subject: string,
  session: { allowed: boolean; mfa: boolean },
): string {
  if (code === 'FORBIDDEN' || code === 'UNAUTHENTICATED') {
    if (!session.allowed) return 'That needs the games.manage permission';
    if (!session.mfa) return 'That needs a two-factor verified session';
    return `${subject} was not saved`;
  }
  if (code === 'CONFLICT') return `${subject} was not saved`;
  if (code === 'VALIDATION_FAILED') return 'That form was not accepted';
  if (code === 'NOT_FOUND') return `${subject} no longer exists`;
  return `${subject} was not saved`;
}
